import { useState } from "react";
import { User, LogOut, ChevronDown, Settings, Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useImpersonation } from "@/hooks/useImpersonation";
import { Link } from "react-router-dom";
import NotificationsDropdown from "./NotificationsDropdown";
import ChangeRequestsBell from "./ChangeRequestsBell";
import ConversationsQuickAccess from "./ConversationsQuickAccess";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isMobile, openMobileSidebar } = useSidebar();
  const { isImpersonating } = useImpersonation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
  };

  return (
    <nav
      className={`bg-white border-b border-gray-200 fixed left-0 right-0 z-50 ${
        isImpersonating
          ? "h-16 top-[calc(40px+var(--safe-top))]"
          : "h-[calc(64px+var(--safe-top))] pt-[var(--safe-top)] top-0"
      }`}
    >
      <div className="h-full px-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Botón hamburguesa para móvil */}
          {isMobile && (
            <button
              onClick={openMobileSidebar}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 -ml-2"
              aria-label="Abrir menú"
            >
              <Menu size={24} />
            </button>
          )}
          <Link to="/" className="flex items-center">
            {/* Móvil: solo las letras RM (el wordmark completo no cabe y se
                deformaba); escritorio: logo horizontal de siempre. */}
            <img
              src="/logo-mark.png"
              alt="ReservaManager"
              className="h-8 sm:hidden"
            />
            <img
              src="/logo-horizontal.png"
              alt="ReservaManager"
              className="h-12 hidden sm:block"
            />
          </Link>
        </div>

        <div className="flex items-center space-x-4">
          {/* Acceso directo a Conversaciones (contador de pendientes) */}
          <ConversationsQuickAccess />

          {/* Solicitudes de cambio de reserva (campanita propia) */}
          <ChangeRequestsBell />

          {/* Notifications */}
          <NotificationsDropdown />

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <User size={18} className="text-primary-600" />
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.role === "owner"
                    ? "Superadmin"
                    : user?.role === "admin"
                      ? "Administrador"
                      : "Staff"}
                </p>
              </div>
              <ChevronDown size={16} className="text-gray-400" />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email}
                    </p>
                  </div>

                  <Link
                    to="/settings"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Settings size={16} className="mr-3" />
                    Configuración
                  </Link>

                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut size={16} className="mr-3" />
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
